import moment from 'moment';
import { replaceData } from '../common';
// Which chars are valid.
// TODO: Find out what these can be? Any restictions from Tryg?
// TODO: Maybe better to defined what they cant be?
const allowedLetters = 'æøåÆØÅ';

/**
 * Validates that an input has the correct minimum and maximum length.
 * @param  {string} input    The input to test.
 * @param  {int} minChars The minimum number of chars the input can have.
 * @param  {int} maxChars The maximum number of chars. 0 means no max length restrictions.
 * @return {boolean}      True on valid input. False on invalid.
 */
export const validateInputLength = (input, minChars, maxChars = 0) => {
  let isValid = input && input.length >= minChars;
  isValid = maxChars > 0 ? isValid && input.length <= maxChars : isValid;
  return isValid;
};

/**
 * Validates that an input is numbers only.
 * @param  {string} input The input to test.
 * @return {boolean}       True on valid input. False on invalid.
 */
export const validateInputNumberOnly = (input) => {
  const regex = new RegExp('^\\d+$');
  return regex.test(input);
};

/**
 * Validates that an input is alphanumeric characters only.
 * @param  {string} input  The input to test.
 * @return {boolean}       True on valid input. False on invalid.
 */
export const validateInputAlphanumericOnly = (input) => {
  const regex = new RegExp(`^[\\w${allowedLetters}]+$`);
  return regex.test(input);
};

/**
 * Validates that an input has the correct email format.
 * @param  {string} input  The input to test.
 * @return {boolean}       True on valid input. False on invalid.
 */
export const validateEmail = (input) => {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(input);
};

/**
 * Validates that an input has the correct phone number format.
 * @param  {string} input  The input to test.
 * @return {boolean}       True on valid input. False on invalid.
 */
export const validatePhoneNumber = (input) => {
  const regex = /\d{8}/;
  return regex.test(input);
};

export const getDateOfBirth = (cpr) => {
  const newCpr = cpr.replace('-', '');
  const regex = new RegExp('[0-9]{1,10}');
  if (regex.test(newCpr)) {
    const date = newCpr.substr(0, 6);
    const DD = date.substr(0, 2);
    const MM = date.substr(2, 2);
    const YY = date.substr(4, 2);
    let C;
    switch (newCpr.substr(6, 1)) {
      case '4':
      case '9':
        if (YY > 36) {
          C = '19';
        } else {
          C = '20';
        }
        break;

      case '5':
      case '6':
      case '7':
      case '8':
        if (YY <= 36) {
          C = '20';
        } else if (YY >= 58) {
          C = '18';
        } else {
          return null;
        }
        break;

      default:
        C = '19';
    }
    return `${C}${YY}-${MM}-${DD}`;
  }

  return null;
};

/**
 * Validates that a CPR has the correct form.
 * @param  {string} input  The input to test.
 * @return {boolean}       True on valid input. False on invalid.
 */
export const validateCpr = (input) => {
  if (!input) {
    return true;
  }

  const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const controlNumbers = [4, 3, 2, 7, 6, 5, 4, 3, 2, 1];
  const useExceptionYears = false;
  const exceptionYears = [
    1960,
    1964,
    1965,
    1966,
    1969,
    1970,
    1980,
    1982,
    1984,
    1985,
    1986,
    1987,
    1988,
    1989,
    1990,
  ];
  const newInput = input.replace('-', '');

  if (!newInput || newInput.length < 10) {
    return true;
  }

  const regex = /^\d{10}$/;
  if (regex.test(newInput)) {
    const month = parseInt(newInput.substring(2, 4), 10);
    if (month === 0 || month > 12) {
      return false;
    } // whether the month is valid

    const day = parseInt(newInput.substring(0, 2), 10);
    if (day > daysInMonth[month - 1]) {
      return false;
    } // whether the day is valid

    const dob = getDateOfBirth(newInput);
    if (!dob) {
      return false;
    }
    const year = dob.substring(0, 4);

    // Check for exception years where cpr is not modules 11. We will disable it for now.
    if (useExceptionYears && day === 1 && month === 1 && exceptionYears.indexOf(year)) {
      return true;
    }

    // Check for modulus 11.
    const components = newInput.split('');
    let sum = 0;
    components.forEach((component, index) => {
      sum += component * controlNumbers[index];
    });

    return sum % 11 === 0;
  }

  return false;
};

/**
 * Validates that a value is NOT set.
 * @param   {string} input   The input value to test on.
 * @param   {array} values   Array of values that cannot be present defined in the validator.
 * @returns {boolean}        TRUE if the input is not in the values array.
 */
export const validateBlocker = (input, values) => {
  return !values.includes(input);
};

/**
 * Validates that values is set in the form.
 * @param   {string}  formValues  The for values.
 * @param   {array}   formFields  Array of field that should be present and truthy in the form.
 * @returns {boolean}             TRUE if the input is not in the values array.
 */
export const formHasValues = (formValues, formFields) => {
  let validated = true;
  formFields.forEach(field => {
    if (!Object.hasOwnProperty.call(formValues, field) || !formValues[field]) {
      validated = false;
    }
  });
  return validated;
};

/**
 * Validates that values is set in the form.
 * @param   {string}  formValues  The for values.
 * @param   {array}   formFields  Array of field that should be present and truthy in the form.
 * @returns {boolean}             TRUE if the input is not in the values array.
 */
export const formHasAnyValues = (formValues, formFields) => {
  let validated = false;
  formFields.forEach(field => {
    if (formValues[field] === true) {
      validated = true;
    }
  });
  return validated;
};

export const getDate = (date, values, dateFormat) => {
  const newDateFormat = dateFormat || 'DD-MM-YYYY';
  const newDate = replaceData(date, values, false, false, false, true);
  const dateCheck = new RegExp(newDateFormat.replace(/(D|M|Y)/g, '[0-9]'));
  let result;

  switch (newDate) {
    case 'today':
      return moment(moment().format(newDateFormat), newDateFormat);

    case 'yesterday':
      result = moment(moment().format(newDateFormat), newDateFormat);
      return result.subtract(1, 'days');

    case 'tomorrow':
      result = moment(moment().format(newDateFormat), newDateFormat);
      return result.add(1, 'days');

    default:
      // Check for valid input date.
      if (dateCheck.test(newDate)) {
        return moment(newDate, newDateFormat);
      }
      return false;
  }
};

/**
 * Validates that an input has the correct phone number format.
 * @param  {string} input       The input to test.
 * @param  {object} formValues  The for values.
 * @param  {string} maxDate
 * @param  {string} dateFormat
 * @return {boolean}            True on valid input. False on invalid.
 */
export const validateMaxDate = (input, formValues, maxDate, dateFormat) => {
  const inputDate = getDate(input, formValues, dateFormat);
  const date = getDate(maxDate, formValues, dateFormat);
  return inputDate && inputDate.isValid() && date &&
    (date.isAfter(inputDate) || inputDate.valueOf() === date.valueOf());
};

/**
 * Validates that an input has the correct phone number format.
 * @param  {string} input  The input to test.
 * @param  {object} formValues
 * @param  {string} minDate
 * @param  {string} dateFormat
 * @return {boolean}       True on valid input. False on invalid.
 */
export const validateMinDate = (input, formValues, minDate, dateFormat) => {
  const inputDate = getDate(input, formValues, dateFormat);
  const date = getDate(minDate, formValues, dateFormat);
  return inputDate && inputDate.isValid() && date &&
    (date.isBefore(inputDate) || inputDate.valueOf() === date.valueOf());
};


// WEBPACK FOOTER //
// ./src/utilities/form/formValidators.js